import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { usePostingSurfacePresence } from '@/features/notifications/hooks/usePostingSurfacePresence';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/features/shared/constants/permissions';
import { useReceivingStore } from '../store/receivingStore';
import { ReceivingStatusEnum, PostingStatusEnum, DocumentStatus, DeliveryTypeEnum, PpeComplianceEnum, PutAwayHistoryStatusEnum } from '../types/receiving.types';
import { ItemTypeBadge } from '../components/ItemTypeBadge';
import { ReceivingCorrectionSheet } from '../components/ReceivingCorrectionSheet';
import type { PutAwayHistoryDto, EditReceivedQuantityRequest } from '../types/receiving.types';
import { receivingStatusConfig } from '../components/ReceivingStatusBadge';
import { ArrowLeft, AlertCircle, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Upload, Pencil, ExternalLink, Undo2, Loader2 } from 'lucide-react';
import { receivingService } from '../services/receivingService';
import { toast } from 'sonner';
import { ActivityDock } from '@features/activity';
import { DetailPageLayout } from '@/components/layouts/DetailPageLayout';
import { PostingRecoveryContent } from '@shared/posting/PostingRecoveryContent';
import { itemReceiptRecoveryView } from '@shared/posting/postingRecovery';
import { PostingArrivalSurface } from '@/features/notifications/components/PostingArrivalSurface';

const DELIVERY_TYPE_LABELS: Record<DeliveryTypeEnum, string> = {
  [DeliveryTypeEnum.Local]: 'Local',
  [DeliveryTypeEnum.ImportRawMaterials]: 'Import – Raw Materials',
  [DeliveryTypeEnum.ImportSpareParts]: 'Import – Spare Parts',
  [DeliveryTypeEnum.Projects]: 'Projects',
};

const PPE_COMPLIANCE_LABELS: Record<PpeComplianceEnum, string> = {
  [PpeComplianceEnum.Yes]: 'Yes',
  [PpeComplianceEnum.No]: 'No',
  [PpeComplianceEnum.NotApplicable]: 'N/A',
};

function formatTimeTo12Hour(time?: string | null): string | undefined {
  if (!time) return undefined;
  const [hoursStr, minutesStr] = time.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = minutesStr ?? '00';
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes} ${period}`;
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString();
}

/** Net (post-reversal) quantity for one put-away row — adjustments/undos leave the history row
 *  in place and bump quantityReversed, so gross `quantity` overstates what's actually binned.
 *  Shared by both progress bars (card + compact) so they can't drift (86d3rrgt5 / 86d39... A2). */
function effectiveQty(h: PutAwayHistoryDto): number {
  return Math.max(0, h.quantity - (h.quantityReversed ?? 0));
}

/** 86d3rrgt5 (A3) — comma-joined list of bins that still hold NET stock; fully-reversed
 *  (net-zero) bins are excluded so the column never points a picker at an emptied bin. */
function netStockBins(histories: PutAwayHistoryDto[]): string {
  const netByBin = new Map<string, number>();
  for (const h of histories) netByBin.set(h.binCode, (netByBin.get(h.binCode) ?? 0) + effectiveQty(h));
  return [...netByBin.entries()].filter(([, net]) => net > 0).map(([bin]) => bin).join(', ');
}

export function ReceivingDetailPage() {
  usePageTitle('Receiving Detail');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // N8 — see PicklistDetailPage: a focused surface suppresses its own outcome toast.
  usePostingSurfacePresence('posting.ir', id ?? null);
  const {
    selectedDocument,
    isLoadingDetail,
    detailError,
    fetchDocumentById,
    cancelDocument,
    resyncDocument,
    postReceivingDocument,
    editReceivedQuantity,
    updateTimeOut,
    isSubmitting,
  } = useReceivingStore();

  // M4 (86d44z223) — Recent. On RESOLVE, and above this component's early returns.
  useRecordVisit('receiving', selectedDocument?.id, selectedDocument?.docNo);

  const canAdjust = usePermission(PERMISSIONS.RECEIVING_ADJUST);
  const canCreate = usePermission(PERMISSIONS.RECEIVING_CREATE);
  // 86d3rrgt5 — gate post-funnel affordances; the API carries [HasPermission("receiving.post")]
  // on post / retry-posting / cancel-posting, so a receiving.view-only user must not see them.
  const canPost = usePermission(PERMISSIONS.RECEIVING_POST);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editTimeOutOpen, setEditTimeOutOpen] = useState(false);
  const [timeOutValue, setTimeOutValue] = useState('');
  const [postDialogOpen, setPostDialogOpen] = useState(false);

  // 86d3tdbnn — correction side sheet. We keep only the target item id and derive the live item from
  // selectedDocument, so the sheet reflects fresh data after a save/refetch rather than a stale snapshot.
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionItemId, setCorrectionItemId] = useState<number | null>(null);

  useEffect(() => {
    if (id) fetchDocumentById(Number(id));
  }, [id, fetchDocumentById]);

  async function handleCancel() {
    if (!selectedDocument) return;
    try {
      await cancelDocument(selectedDocument.id, { reason: cancelReason || undefined });
      toast.success('Receiving document cancelled');
      setCancelDialogOpen(false);
      setCancelReason('');
    } catch {
      // error shown via store
    }
  }

  async function handlePost() {
    if (!selectedDocument) return;
    try {
      // 86d3kwtar — 202 claim; the store optimistically flips to Posting and polls until it settles.
      await postReceivingDocument(selectedDocument.id, { ppeCompliance: selectedDocument.ppeCompliance });
      toast.success('Posting started — creating the Item Receipt in NetSuite…');
      setPostDialogOpen(false);
    } catch {
      // claim rejected (e.g. a guard 400) — error shown via store
    }
  }

  // 86d3mnuxe funnel — resolve a failed posting intent from the banner. Retry re-arms the same job
  // (frozen tranDate; live rebuild + adopt serve both the plain retry and the reached reconcile);
  // Cancel closes it (Failed → Cancelled) so a fresh Post is accepted — the BE refuses the cancel
  // when the post may have reached NetSuite (its message says to retry/reconcile instead).
  const [isResolvingPosting, setIsResolvingPosting] = useState(false);
  async function handleRetryPosting() {
    if (!selectedDocument) return;
    setIsResolvingPosting(true);
    try {
      await receivingService.retryPosting(selectedDocument.id);
      toast.success('Retrying the Item Receipt post…');
      await fetchDocumentById(selectedDocument.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to retry the posting');
    } finally {
      setIsResolvingPosting(false);
    }
  }
  async function handleCancelPosting() {
    if (!selectedDocument) return;
    setIsResolvingPosting(true);
    try {
      await receivingService.cancelPosting(selectedDocument.id);
      toast.success('Posting cancelled — you can post again.');
      await fetchDocumentById(selectedDocument.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to cancel the posting');
    } finally {
      setIsResolvingPosting(false);
    }
  }

  async function handleResync() {
    if (!selectedDocument) return;
    try {
      const result = await resyncDocument(selectedDocument.id);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.warning(result.message);
      }
    } catch {
      // error shown via store
    }
  }

  // 86d3tdbnn — the correction sheet builds the request; the page owns the API call + toasts. Rethrow
  // on failure so the sheet keeps itself open for a retry (recoverable-failure UX gate).
  async function handleCorrectionSubmit(itemId: number, request: EditReceivedQuantityRequest) {
    if (!selectedDocument) return;
    try {
      await editReceivedQuantity(selectedDocument.id, itemId, request);
      toast.success('Correction saved');
    } catch (e) {
      const msg =
        useReceivingStore.getState().submitError ??
        (e instanceof Error ? e.message : 'Failed to save the correction');
      toast.error(msg);
      throw e;
    }
  }

  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading receiving document...
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mro/receiving')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{detailError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!selectedDocument) return null;

  const doc = selectedDocument;
  const isDraft = doc.documentStatus === DocumentStatus.Draft;
  const isPending = doc.receivingStatus === ReceivingStatusEnum.PendingPutAway;
  const isPartiallyPutAway = doc.receivingStatus === ReceivingStatusEnum.PartiallyPutAway;
  const isPutAwayComplete = doc.receivingStatus === ReceivingStatusEnum.PutAwayComplete;
  // Only non-terminal statuses are eligible for cancellation (Completed/Cancelled are terminal).
  const isCancellableStatus = isPending || isPartiallyPutAway || isPutAwayComplete;
  // Doc is in "clean" state if every item has zero received qty and zero put-away qty.
  const allItemsClean = doc.items.every((i) => i.receivedQuantity === 0 && i.quantityPutAway === 0);
  // Cancellation: a draft (Stage 1 not yet completed) cancels freely. A submitted document
  // (Stage 1 done) can only be cancelled once every item has been zeroed-out via Adjust
  // Quantity — mirrors the backend guard in CancelReceivingDocumentAsync.
  const canCancel = canAdjust && isCancellableStatus && (isDraft || allItemsClean);
  // Edit qty: allowed at non-terminal statuses only. Completed/Closed docs (IR posted
  // to NetSuite) are terminal in DOP — post-close adjustments are handled outside DOP.
  // 86d3rrgf4 — also mirror the server edit-guard (EnsureNotPostingAsync): block while a post is
  // in-flight or after a REACHED failure (an IR exists — editing would diverge from NetSuite), but
  // keep editing available after a NotReached failure, where editing IS the intended recovery.
  const isPostingInFlight = doc.postingStatus === PostingStatusEnum.Posting;
  const isReachedFailure =
    doc.postingStatus === PostingStatusEnum.PostingFailed && (doc.postingReach ?? 0) >= 2;
  const canEditQuantity =
    canAdjust &&
    (isPending || isPartiallyPutAway || isPutAwayComplete) &&
    !isPostingInFlight &&
    !isReachedFailure;
  // TimeOut is editable until the Item Receipt posts (NetSuiteItemReceiptId set) — 86d36yk0w.
  const canEditTimeOut = canCreate && !doc.netSuiteItemReceiptId && doc.documentStatus !== DocumentStatus.Cancelled;

  // 86d3tdbnn — the correction sheet's target item, resolved live from the document so it stays
  // fresh across refetches (null until a row's Correct action sets the id).
  const correctionItem =
    correctionItemId != null ? doc.items.find((i) => i.id === correctionItemId) ?? null : null;

  const allPutAwayHistories = doc.items.flatMap((item) =>
    item.putAwayHistories.map((h) => ({
      ...h,
      itemId: item.id,
      itemCode: item.itemCode,
      itemDescription: item.itemDescription,
      unitOfMeasure: item.unitOfMeasure,
    }))
  );


  return (
    <DetailPageLayout
      selfSizedRail
      rail={<ActivityDock entityType="Receiving" entityId={doc.id} />}
    >
      <div className="space-y-4 p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/mro/receiving')}>
              <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-mono">{doc.docNo}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={receivingStatusConfig[doc.receivingStatus]?.variant ?? 'secondary'}>
                {receivingStatusConfig[doc.receivingStatus]?.label ?? 'Unknown'}
              </Badge>
              {doc.documentStatus === DocumentStatus.Closed && (
                <Badge variant="destructive">Closed</Badge>
              )}
              {doc.documentStatus === DocumentStatus.Cancelled &&
                doc.receivingStatus !== ReceivingStatusEnum.Cancelled && (
                <Badge variant="destructive">Cancelled</Badge>
              )}
              {doc.forAdjustment && (
                <Badge variant="outline" className="border-warning/30 text-warning">
                  <AlertTriangle className="mr-1 h-3 w-3" />
                  For Adjustment
                </Badge>
              )}
              <span className="text-muted-foreground text-sm">
                Received by {doc.receivedByName} on {formatDate(doc.receivedAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (isPending || isPartiallyPutAway) && doc.forAdjustment && (
            <Button variant="outline" size="sm" disabled={isSubmitting} onClick={handleResync}>
              <RefreshCw className="mr-1 h-3 w-3" />
              Resync
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-destructive hover:bg-destructive/10"
              disabled={isSubmitting}
              onClick={() => setCancelDialogOpen(true)}
            >
              <XCircle className="mr-1 h-3 w-3" />
              Cancel
            </Button>
          )}
          {/* Disabled-state tooltip variant: surfaces the option for submitted docs that
              still carry received/put-away quantity, so users understand why cancellation
              isn't available and what to do (use Adjust Quantity first). */}
          {!canCancel && canAdjust && isCancellableStatus && !isDraft && !allItemsClean && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-destructive opacity-50 cursor-not-allowed"
              disabled
              title="Ask a supervisor to reduce the received quantity of every item to zero (Adjust Quantity) before cancelling."
            >
              <XCircle className="mr-1 h-3 w-3" />
              Cancel
            </Button>
          )}
          {canPost && isPutAwayComplete && !doc.forAdjustment && (() => {
            const receiverHasNsEmployee = Boolean(doc.receivedByNetSuiteEmployeeId);
            const isPosting = doc.postingStatus === PostingStatusEnum.Posting;
            // 86d3mnuxe funnel — one posting intent at a time: while the last posting is Failed,
            // Post is disabled and the failed banner's Retry/Cancel is the way forward (the BE
            // claim rejects a fresh post too).
            const isFailed = doc.postingStatus === PostingStatusEnum.PostingFailed;
            return (
              <span
                title={
                  !receiverHasNsEmployee
                    ? `The Stage 1 receiver (${doc.receivedByName}) is not linked to a NetSuite employee. Ask an administrator to link their NetSuite Employee ID before posting.`
                    : isFailed
                      ? 'The last posting failed — retry or cancel it from the banner above first.'
                      : undefined
                }
              >
                <Button
                  size="sm"
                  disabled={isSubmitting || isPosting || isFailed || !receiverHasNsEmployee}
                  onClick={() => setPostDialogOpen(true)}
                >
                  {isPosting ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      Posting…
                    </>
                  ) : (
                    <>
                      <Upload className="mr-1 h-3 w-3" />
                      Post Item Receipt
                    </>
                  )}
                </Button>
              </span>
            );
          })()}
        </div>
      </div>

      {/* 86d3mnuxe funnel — the failed posting intent is resolved HERE (Post is disabled while it
          exists). The same re-arm serves both reach states (live rebuild + adopt branch internally),
          but the LABEL discloses which: postingReach ≥ 2 → "Reconcile" (records the Item Receipt
          NetSuite already accepted — no re-post) and Cancel hides (the BE refuses it — abandoning
          would orphan the record). NotReached → Retry or Cancel. */}
      {/* N8 (86d3w2kez): reach decision and copy come from the SHARED `PostingRecoveryContent`. This
          block used to hand-roll `(doc.postingReach ?? 0) >= 2` — one of four private copies of the
          rule that decides whether a click DUPLICATES a record in the live company file.
          ⛔ Do not reintroduce reach logic here.

          ⚠️ Note what `?? 0` means once it reaches the shared view: PostReach.Unknown, NOT NotReached.
          "Nobody established it" and "it definitely never arrived" are different answers, and only the
          second is safe to re-post on. */}
      {canPost && doc.postingStatus === PostingStatusEnum.PostingFailed && (() => {
        // Built once and rendered twice — the inline alert and the floating panel a notification
        // arrival raises are the same object, so they cannot disagree on screen.
        const view = itemReceiptRecoveryView({
          isFailed: true,
          postingReach: doc.postingReach ?? null,
          postingNetSuiteRecordId: doc.postingNetSuiteRecordId ?? null,
          postingLastError: doc.postingFailedReason ?? null,
        });

        return (
          <PostingArrivalSurface
            views={[view]}
            // IR, like MIS, has ONE server-side re-arm serving both verbs; the shared core picks the
            // label that discloses which of the two the user is doing.
            onRetry={() => void handleRetryPosting()}
            onReconcile={() => void handleRetryPosting()}
            onCancel={() => void handleCancelPosting()}
            isBusy={isResolvingPosting}
          >
            <Alert variant="destructive">
              <AlertDescription>
                <PostingRecoveryContent
                  view={view}
                  onRetry={() => void handleRetryPosting()}
                  onReconcile={() => void handleRetryPosting()}
                  onCancel={() => void handleCancelPosting()}
                  isBusy={isResolvingPosting}
                />
              </AlertDescription>
            </Alert>
          </PostingArrivalSurface>
        );
      })()}

      {/* ForAdjustment Alert */}
      {doc.forAdjustment && (
        <Alert className="border-warning/30 bg-warning/10 text-warning">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertDescription>
            This document is flagged for adjustment. The linked Purchase Order may have changed in NetSuite.
            Please resync to verify before proceeding with put-away.
          </AlertDescription>
        </Alert>
      )}

      {/* Cancellation Info */}
      {doc.receivingStatus === ReceivingStatusEnum.Cancelled && doc.cancelledAt && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            Cancelled on {new Date(doc.cancelledAt).toLocaleString()}
            {doc.cancelledByName && ` by ${doc.cancelledByName}`}
          </AlertDescription>
        </Alert>
      )}

      {/* Delivery Information */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery Information</CardTitle>
          <CardDescription>
            PO:{' '}
            {doc.purchaseOrderId ? (
              <Link
                to={`/mro/purchase-orders/${doc.purchaseOrderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline underline-offset-2 decoration-muted-foreground/50 hover:decoration-foreground transition-colors"
              >
                {doc.purchaseOrderNumber}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </Link>
            ) : (
              doc.purchaseOrderNumber
            )}
            {' '}· {doc.vendorName}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <DetailRow label="Delivery Number" value={doc.deliveryNumber} />
          <DetailRow label="Delivery Type" value={DELIVERY_TYPE_LABELS[doc.deliveryType]} />
          <DetailRow label="Delivery Date" value={doc.docDate ? new Date(doc.docDate).toLocaleDateString() : undefined} />
          <DetailRow label="Time In" value={formatTimeTo12Hour(doc.timeIn)} />
          {canEditTimeOut ? (
            <div>
              <p className="text-xs text-muted-foreground">Time Out</p>
              <div className="flex items-center gap-1.5">
                <p className="text-sm">{formatTimeTo12Hour(doc.timeOut) ?? '—'}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="Edit Time Out"
                  onClick={() => {
                    setTimeOutValue(doc.timeOut?.slice(0, 5) ?? '');
                    setEditTimeOutOpen(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <DetailRow label="Time Out" value={formatTimeTo12Hour(doc.timeOut)} />
          )}
          <DetailRow label="Letter of Credit" value={doc.letterOfCredit} />
          <DetailRow label="Vessel Name" value={doc.vesselName} />
          <DetailRow label="Bill of Lading" value={doc.billOfLading} />
          <DetailRow label="Received By" value={doc.receivedByName} />
          <DetailRow
            label="Received At"
            value={new Date(doc.receivedAt).toLocaleString()}
          />
          <DetailRow label="Remarks" value={doc.remarks} />
          <DetailRow label="PPE Compliance" value={PPE_COMPLIANCE_LABELS[doc.ppeCompliance]} />
        </CardContent>
      </Card>

      {/* Completion Info (Stage 2) */}
      {doc.receivingStatus === ReceivingStatusEnum.Completed && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-success" />
              Put-Away Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {doc.netSuiteItemReceiptTranId && (
              <div>
                <p className="text-xs text-muted-foreground">Item Receipt</p>
                {doc.netSuiteItemReceiptUrl ? (
                  <a
                    href={doc.netSuiteItemReceiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-mono font-semibold text-info underline underline-offset-2 decoration-info/40 hover:decoration-info transition-colors"
                  >
                    {doc.netSuiteItemReceiptTranId}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                ) : (
                  <p className="text-sm font-mono font-semibold text-info">
                    {doc.netSuiteItemReceiptTranId}
                  </p>
                )}
              </div>
            )}
            {doc.putAwayCompletedAt && (
              <DetailRow
                label="Completed At"
                value={new Date(doc.putAwayCompletedAt).toLocaleString()}
              />
            )}
            {doc.closedByName && (
              <DetailRow label="Closed By" value={doc.closedByName} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
          <CardDescription>{doc.items.length} line item(s)</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Put Away</TableHead>
                <TableHead>Bins</TableHead>
                <TableHead className="w-[160px]">Progress</TableHead>
                {canEditQuantity && <TableHead className="w-[60px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {doc.items.map((item) => {
                // effectiveQty + netStockBins are module-level (shared with the compact view so they can't drift).
                const postedQty = item.putAwayHistories?.filter(h => h.isPosted).reduce((s, h) => s + effectiveQty(h), 0) ?? 0;
                const unpostedQty = item.putAwayHistories?.filter(h => !h.isPosted).reduce((s, h) => s + effectiveQty(h), 0) ?? 0;
                const pendingQty = item.receivedQuantity - item.quantityPutAway;
                const total = item.receivedQuantity || 1;
                const binCodes = netStockBins(item.putAwayHistories);

                return (
                <TableRow key={item.id} className={item.isPutAway ? 'bg-success/10' : undefined}>
                  <TableCell>
                    <div>
                      <span className="font-mono">{item.itemCode}</span>
                      <p className="text-xs text-muted-foreground">{item.itemDescription}</p>
                      {item.notes && (
                        <p className="text-xs text-muted-foreground italic">{item.notes}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ItemTypeBadge item={item} />
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={item.receivedQuantity !== item.purchaseOrderQuantity ? 'text-warning' : ''}>
                      {item.receivedQuantity} / {item.purchaseOrderQuantity}
                    </span>
                    <span className="text-xs text-muted-foreground ml-1">{item.unitOfMeasure}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={item.isPutAway ? 'text-success' : item.quantityPutAway > 0 ? 'text-warning' : 'text-muted-foreground'}>
                      {item.quantityPutAway} / {item.receivedQuantity}
                    </span>
                    <span className="text-xs text-muted-foreground ml-1">{item.unitOfMeasure}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.isServiceItem ? (
                      <span className="text-sm">N/A</span>
                    ) : binCodes ? (
                      binCodes
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.isServiceItem ? (
                      <span className="text-muted-foreground text-sm">N/A</span>
                    ) : (
                      <div className="space-y-1">
                        <div className="w-full h-2 bg-border rounded-full overflow-hidden flex">
                          {postedQty > 0 && (
                            <div
                              className="h-full bg-success transition-all"
                              style={{ width: `${(postedQty / total) * 100}%` }}
                            />
                          )}
                          {unpostedQty > 0 && (
                            <div
                              className="h-full bg-warning transition-all"
                              style={{ width: `${(unpostedQty / total) * 100}%` }}
                            />
                          )}
                          {pendingQty > 0 && (
                            <div
                              className="h-full bg-muted-foreground transition-all"
                              style={{ width: `${(pendingQty / total) * 100}%` }}
                            />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {postedQty > 0 && postedQty === item.receivedQuantity
                            ? `${item.receivedQuantity} / ${item.receivedQuantity} posted`
                            : postedQty > 0
                              ? `${postedQty} posted · ${unpostedQty} put away / ${item.receivedQuantity}`
                              : `${item.quantityPutAway} / ${item.receivedQuantity} put away`}
                        </p>
                      </div>
                    )}
                  </TableCell>
                  {canEditQuantity && (
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={isSubmitting}
                              aria-label="Correct received quantity and put-away"
                              onClick={() => {
                                setCorrectionItemId(item.id);
                                setCorrectionOpen(true);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Correct received quantity &amp; put-away</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Put-Away History */}
      <Card>
        <CardHeader>
          <CardTitle>Put-Away History</CardTitle>
          <CardDescription>Timeline of all put-away actions</CardDescription>
        </CardHeader>
        <CardContent>
          {allPutAwayHistories.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No put-away actions recorded yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date/Time</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Bin</TableHead>
                  <TableHead>Lot/Serial</TableHead>
                  <TableHead>Put Away By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...allPutAwayHistories]
                  .sort((a, b) => new Date(b.putAwayAt).getTime() - new Date(a.putAwayAt).getTime())
                  .map((h) => {
                    const isReversed = h.status === PutAwayHistoryStatusEnum.Reversed;
                    const isPartiallyReversed = !isReversed && (h.quantityReversed ?? 0) > 0;
                    const remainingActive = h.quantity - (h.quantityReversed ?? 0);
                    return (
                      <TableRow key={h.id} className={isReversed ? 'opacity-50' : undefined}>
                        <TableCell>{new Date(h.putAwayAt).toLocaleString()}</TableCell>
                        <TableCell>
                          <div>
                            <span className={`font-mono ${isReversed ? 'line-through' : ''}`}>{h.itemCode}</span>
                            <p className="text-xs text-muted-foreground">{h.itemDescription}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {isReversed ? (
                            <span className="line-through">{h.quantity} {h.unitOfMeasure}</span>
                          ) : isPartiallyReversed ? (
                            <span>
                              {remainingActive} {h.unitOfMeasure}
                              <span className="ml-1 text-xs text-muted-foreground">(of {h.quantity}; {h.quantityReversed} reversed)</span>
                            </span>
                          ) : (
                            <>{h.quantity} {h.unitOfMeasure}</>
                          )}
                        </TableCell>
                        <TableCell className="font-mono">{h.binCode}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {[
                            h.lotNumber && `Lot: ${h.lotNumber}`,
                            h.expirationDate && `Exp: ${new Date(h.expirationDate).toLocaleDateString()}`,
                            h.serialNumber && `SN: ${h.serialNumber}`,
                          ].filter(Boolean).join(' · ') || '—'}
                        </TableCell>
                        <TableCell>
                          <div>
                            {h.putAwayByName}
                            {isReversed && h.reversedAt && (
                              <p className="mt-0.5 text-xs text-muted-foreground flex items-center gap-1">
                                <Undo2 className="h-3 w-3" />
                                Reversed by {h.reversedByName ?? '?'} on {new Date(h.reversedAt).toLocaleString()}
                                {h.reversedByAdjustmentLogId && ` (log #${h.reversedByAdjustmentLogId})`}
                              </p>
                            )}
                            {isPartiallyReversed && h.reversedAt && (
                              <p className="mt-0.5 text-xs text-muted-foreground flex items-center gap-1">
                                <Undo2 className="h-3 w-3" />
                                {h.quantityReversed} {h.unitOfMeasure} reversed by {h.reversedByName ?? '?'}
                                {h.reversedByAdjustmentLogId && ` (log #${h.reversedByAdjustmentLogId})`}
                              </p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={editTimeOutOpen} onOpenChange={setEditTimeOutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Out</DialogTitle>
            <DialogDescription>
              Correct the receiver Time Out. Allowed only until the Item Receipt is posted to NetSuite.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="edit-timeout">Time Out</Label>
            <Input
              id="edit-timeout"
              type="time"
              value={timeOutValue}
              onChange={(e) => setTimeOutValue(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTimeOutOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!timeOutValue || isSubmitting}
              onClick={async () => {
                try {
                  await updateTimeOut(doc.id, timeOutValue);
                  toast.success('Time Out updated');
                  setEditTimeOutOpen(false);
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : 'Failed to update Time Out');
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Receiving Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel <strong>{doc.docNo}</strong>? This action cannot be undone.
              No NetSuite posting will occur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancelReason">
              Reason{!isDraft && <span className="text-destructive"> (required)</span>}
              {isDraft && <span className="text-muted-foreground"> (optional)</span>}
            </Label>
            <Textarea
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation"
              rows={3}
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={isSubmitting || (!isDraft && !cancelReason.trim())}
            >
              {isSubmitting ? 'Cancelling...' : 'Yes, Cancel Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Item Receipt Confirmation Dialog */}
      <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Post Item Receipt to NetSuite</DialogTitle>
            <DialogDescription>
              This will create an Item Receipt in NetSuite for <strong>{doc.docNo}</strong>.
              Once posted, this action <strong>cannot be undone</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded border p-3 space-y-1">
            <p className="text-sm text-muted-foreground">
              PO:{' '}
              {doc.purchaseOrderId ? (
                <Link
                  to={`/mro/purchase-orders/${doc.purchaseOrderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 underline underline-offset-2 decoration-muted-foreground/50 hover:decoration-foreground transition-colors"
                >
                  {doc.purchaseOrderNumber}
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </Link>
              ) : (
                doc.purchaseOrderNumber
              )}
              {' '}· {doc.vendorName}
            </p>
            <p className="text-sm text-muted-foreground">
              {doc.items.length} item(s) · {doc.items.reduce((sum, i) => sum + i.receivedQuantity, 0)} units total
            </p>
          </div>
          <div className="min-h-0">
            <div className="max-h-72 overflow-y-auto rounded border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-2 py-1.5">Item</th>
                    <th className="px-2 py-1.5">Type</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5">Bins</th>
                    <th className="px-2 py-1.5 w-[100px]">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.items.map((item) => {
                    // effectiveQty + netStockBins are module-level (shared with the card view so they can't drift).
                    const postedQty = item.putAwayHistories?.filter(h => h.isPosted).reduce((s, h) => s + effectiveQty(h), 0) ?? 0;
                    const unpostedQty = item.putAwayHistories?.filter(h => !h.isPosted).reduce((s, h) => s + effectiveQty(h), 0) ?? 0;
                    const total = item.receivedQuantity || 1;
                    const binCodes = netStockBins(item.putAwayHistories);

                    return (
                    <tr key={item.id} className="border-t align-top">
                      <td className="px-2 py-1.5">
                        <p className="font-medium">{item.itemCode}</p>
                        {item.itemDescription && (
                          <p className="text-xs text-muted-foreground truncate max-w-[220px]">{item.itemDescription}</p>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <ItemTypeBadge item={item} className="text-[10px] px-1 py-0" />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                        {item.receivedQuantity} <span className="text-xs text-muted-foreground">{item.unitOfMeasure}</span>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">
                        {item.isServiceItem ? (
                          'N/A'
                        ) : binCodes ? (
                          binCodes
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        {item.isServiceItem ? (
                          <span className="text-xs text-muted-foreground">N/A</span>
                        ) : (
                          <div className="flex h-2 w-full overflow-hidden rounded-full bg-border">
                            {postedQty > 0 && (
                              <div
                                className="h-full bg-success transition-all"
                                style={{ width: `${(postedQty / total) * 100}%` }}
                              />
                            )}
                            {unpostedQty > 0 && (
                              <div
                                className="h-full bg-warning transition-all"
                                style={{ width: `${(unpostedQty / total) * 100}%` }}
                              />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handlePost} disabled={isSubmitting}>
              {isSubmitting ? 'Posting...' : 'Confirm & Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 86d3tdbnn — Correction side sheet (replaces the old Adjust Quantity dialog). Edits received
          quantity + put-away placements together with the reconciliation rule made explicit. */}
      <ReceivingCorrectionSheet
        open={correctionOpen}
        onOpenChange={setCorrectionOpen}
        item={correctionItem}
        poCap={correctionItem?.purchaseOrderQuantity ?? 0}
        readOnly={isPostingInFlight || isReachedFailure}
        submitting={isSubmitting}
        onSubmit={handleCorrectionSubmit}
      />
      </div>
    </DetailPageLayout>
  );
}
